import type { Action, Resource, PermissionSet, Role } from '@buildbee/contracts';

/**
 * BuildBee's built-in role defaults. Companies change them per role on the Roles & permissions page
 * (stored as role policies, see ./hierarchy.ts); these sets are the starting point and never change
 * at runtime.
 */

export type PermissionString = string;

export const ROLE_DEFAULTS: Record<Role, PermissionSet> = {
  owner: { allow: ['*'], deny: [] },
  admin: { allow: ['*'], deny: ['billing:manage', 'workspace:delete'] },
  sales: {
    allow: [
      'workspace:view',
      'member:view',
      'company:*',
      'contact:*',
      'pipeline:view',
      'deal:*',
      'activity:*',
      'task:*',
      'estimate:*',
      'proposal:*',
      'template:*',
      'report:view',
      'document:view',
      'document:create',
      'project:view',
      'project:create',
      'bid_package:view',
      'bid_package:create',
      'bid_package:update',
      'bid:*',
      'email:*',
      'custom_field:view',
      'portal:view',
    ],
    deny: ['company:delete', 'contact:delete'],
  },
  pm: {
    allow: [
      'workspace:view',
      'member:view',
      'company:view',
      'company:create',
      'company:update',
      'contact:view',
      'contact:create',
      'contact:update',
      'pipeline:view',
      'deal:view',
      'activity:*',
      'task:*',
      'estimate:view',
      'estimate:create',
      'estimate:update',
      'proposal:view',
      'template:view',
      'report:view',
      'project:*',
      'project_member:*',
      'schedule:*',
      'document:*',
      'drawing:*',
      'markup:*',
      'rfi:*',
      'submittal:*',
      'daily_log:*',
      'photo:*',
      'punch_item:*',
      'selection:*',
      'message:*',
      'bid_package:*',
      'bid:*',
      'budget:*',
      'change_order:view',
      'change_order:create',
      'change_order:update',
      'invoice:view',
      'invoice:create',
      'invoice:update',
      'payment:view',
      'lien_waiver:*',
      'portal:*',
      'email:*',
      'custom_field:view',
    ],
    deny: ['project:delete'],
  },
  field: {
    allow: [
      'workspace:view',
      'member:view',
      'contact:view',
      'company:view',
      'project:view',
      'schedule:view',
      'schedule:update',
      'document:view',
      'document:create',
      'drawing:view',
      'markup:view',
      'markup:create',
      'markup:update',
      'rfi:view',
      'rfi:create',
      'submittal:view',
      'daily_log:view',
      'daily_log:create',
      'daily_log:update',
      'photo:view',
      'photo:create',
      'photo:update',
      'punch_item:view',
      'punch_item:create',
      'punch_item:update',
      'task:view',
      'task:create',
      'task:update',
      'activity:view',
      'activity:create',
      'message:view',
      'message:create',
    ],
    deny: [],
    ownRecordsOnly: false,
  },
  sub: {
    allow: [
      'workspace:view',
      'project:view',
      'schedule:view',
      'document:view',
      'drawing:view',
      'markup:view',
      'rfi:view',
      'rfi:create',
      'submittal:view',
      'submittal:create',
      'submittal:update',
      'photo:view',
      'photo:create',
      'punch_item:view',
      'punch_item:update',
      'bid_package:view',
      'bid:view',
      'bid:create',
      'bid:update',
      'lien_waiver:view',
      'lien_waiver:update',
      'task:view',
      'task:update',
    ],
    deny: [],
    ownRecordsOnly: true,
  },
  client: {
    allow: ['portal:view'],
    deny: [],
  },
};

/** Resources whose access can be narrowed or widened per project. */
export const PROJECT_SCOPED_RESOURCES: ReadonlySet<Resource> = new Set<Resource>([
  'project',
  'project_member',
  'schedule',
  'document',
  'drawing',
  'markup',
  'rfi',
  'submittal',
  'daily_log',
  'photo',
  'punch_item',
  'selection',
  'message',
  'budget',
  'change_order',
  'invoice',
  'payment',
  'lien_waiver',
  'bid_package',
  'bid',
  'task',
  'activity',
]);

/** Roles that only see projects they are members of. */
export const PROJECT_SCOPED_ROLES: ReadonlySet<Role> = new Set<Role>(['field', 'sub']);

export function matches(pattern: string, resource: Resource, action: Action): boolean {
  if (pattern === '*') return true;
  const [r, a] = pattern.split(':');
  if (r === undefined || a === undefined) return false;
  const resourceOk = r === '*' || r === resource;
  const actionOk = a === '*' || a === action || (a === 'manage' && r !== '*');
  return resourceOk && actionOk;
}

/**
 * A pattern list compiled to set lookups (same meaning as `matches`): `*` and `*:*` match all,
 * `r:*` and `r:manage` every action on r, `*:a` action a on every resource, `r:a` one cell.
 * Checks run on every request, so this keeps them O(1) instead of re-splitting strings.
 */
type CompiledPatterns = { size: number; all: boolean; cells: Set<string>; resources: Set<string>; actions: Set<string> };
const compiled = new WeakMap<readonly string[], CompiledPatterns>();

function compilePatterns(list: readonly string[]): CompiledPatterns {
  const hit = compiled.get(list);
  if (hit && hit.size === list.length) return hit;
  const out: CompiledPatterns = { size: list.length, all: false, cells: new Set(), resources: new Set(), actions: new Set() };
  for (const p of list) {
    if (p === '*') {
      out.all = true;
      continue;
    }
    const [r, a] = p.split(':');
    if (r === undefined || a === undefined) continue;
    if (r === '*') {
      if (a === '*') out.all = true;
      else out.actions.add(a);
    } else if (a === '*' || a === 'manage') out.resources.add(r);
    else out.cells.add(`${r}:${a}`);
  }
  compiled.set(list, out);
  return out;
}

export function anyMatch(list: readonly string[] | undefined | null, resource: Resource, action: Action): boolean {
  if (!list?.length) return false;
  const c = compilePatterns(list);
  return c.all || c.resources.has(resource) || c.actions.has(action) || c.cells.has(`${resource}:${action}`);
}
